/// <reference no-default-lib="true" />
/// <reference lib="esnext" />
/// <reference lib="dom" />

import Panzoom, { PanzoomEventDetail } from '@panzoom/panzoom'

export function initProductDetail() {
  const productDetails = Array.from(
    document.querySelectorAll('.product-detail'),
  ) as HTMLElement[]

  for (const productDetail of productDetails) {
    initOne(productDetail)
  }
}

function initOne(productDetail: HTMLElement) {
  if (productDetail.dataset.hydrated) return
  productDetail.dataset.hydrated = 'true'

  const zoomDialog = productDetail.querySelector(
    '.product-detail__packshot-zoom-dialog',
  ) as HTMLDialogElement | null

  if (!zoomDialog) {
    return
  }

  const packshot = productDetail.querySelector(
    '.product-detail__packshot .image',
  ) as
    | HTMLElement
    | null

  packshot?.addEventListener('click', () => zoomDialog.showModal())

  const zoomReset = productDetail.querySelector(
    '.product-detail__packshot-zoom-dialog-zoom-reset',
  ) as HTMLElement | null
  const zoomOut = productDetail.querySelector(
    '.product-detail__packshot-zoom-dialog-zoom-out',
  ) as HTMLElement | null
  const zoomIn = productDetail.querySelector(
    '.product-detail__packshot-zoom-dialog-zoom-in',
  ) as HTMLElement | null
  const zoomEl = productDetail.querySelector(
    '.product-detail__packshot-zoom-dialog-packshot img',
  ) as HTMLElement | null
  const zoomContainer = zoomEl?.parentElement?.parentElement

  if (
    !zoomReset || !zoomOut || !zoomIn || !zoomEl || !zoomContainer || !packshot
  ) {
    return
  }

  const maxScale = 5
  const minScale = 1

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  )

  const getAnimationStyles = () => {
    return prefersReducedMotion.matches
      ? { animate: false, easing: 'none', duration: 0 }
      : { animate: true }
  }

  const options = {
    maxScale,
    minScale,
    panOnlyWhenZoomed: true,
    overflow: 'visible',
    ...getAnimationStyles(),
  }

  const panzoom = Panzoom(zoomEl, options)

  prefersReducedMotion.addEventListener('change', () => {
    panzoom.setOptions({
      ...options,
      ...getAnimationStyles(),
    })

    panzoom.reset()
  })

  zoomContainer.addEventListener('wheel', panzoom.zoomWithWheel)
  zoomReset.addEventListener('click', panzoom.reset)
  zoomOut.addEventListener('click', panzoom.zoomOut)
  zoomIn.addEventListener('click', panzoom.zoomIn)
  zoomDialog.addEventListener('close', panzoom.reset)

  // @ts-ignore: wtf
  zoomEl.addEventListener('panzoomchange', (e: CustomEvent) => {
    const detail: PanzoomEventDetail = e.detail

    if (detail.scale <= minScale) {
      zoomReset.setAttribute('disabled', 'true')
      zoomOut.setAttribute('disabled', 'true')
    } else {
      zoomReset.removeAttribute('disabled')
      zoomOut.removeAttribute('disabled')
    }

    if (detail.scale >= maxScale) {
      zoomIn.setAttribute('disabled', 'true')
    } else {
      zoomIn.removeAttribute('disabled')
    }

    if (detail.scale === minScale && detail.x !== 0 && detail.y !== 0) {
      panzoom.reset()
    }
  })

  const controlsPrev = zoomDialog.querySelector(
    '.product-detail__controls-nav-arrow--prev',
  ) as HTMLButtonElement | null
  const controlsNext = zoomDialog.querySelector(
    '.product-detail__controls-nav-arrow--next',
  ) as HTMLButtonElement | null
  const controlsCurrent = zoomDialog.querySelector(
    '.product-detail__controls-count-current',
  ) as HTMLElement | null
  const controlsTotal = zoomDialog.querySelector(
    '.product-detail__controls-count-total',
  ) as HTMLElement | null

  const pad = (n: number) => String(n).padStart(2, '0')

  const productGallery = productDetail.querySelector(
    '.product-detail__gallery',
  ) as
    | HTMLElement
    | null

  const packshotImg = packshot.querySelector(
    '.product-detail__packshot .image img',
  ) as HTMLImageElement | null

  let activeIndex = 0

  if (productGallery) {
    const items = Array.from(
      productGallery.querySelectorAll('.product-detail__gallery-item'),
    ) as HTMLElement[]

    const total = items.length
    const isDesktop = globalThis.matchMedia('(min-width: 1280px)')

    if (controlsPrev && controlsNext && controlsCurrent && controlsTotal) {
      controlsCurrent!.textContent = pad(1)
      controlsTotal!.textContent = pad(total)
    }

    if (isDesktop.matches) {
      productGallery.removeAttribute('role')
      for (const item of items) {
        item.removeAttribute('role')
        item.tabIndex = 0
      }
    } else {
      productGallery.setAttribute('role', 'tablist')
      const activeItem = items.find((i) =>
        i.getAttribute('aria-selected') === 'true'
      )
      for (const item of items) {
        item.setAttribute('role', 'tab')
        item.tabIndex = item === activeItem ? 0 : -1
      }
    }

    productGallery.addEventListener('keydown', (e: KeyboardEvent) => {
      if (isDesktop.matches) {
        return
      }

      const target = e.target as HTMLElement

      if (!target.matches('.product-detail__gallery-item')) {
        return
      }

      const currentIndex = items.indexOf(target)
      let newIndex = currentIndex

      switch (e.key) {
        case 'ArrowRight':
          newIndex = currentIndex + 1
          break
        case 'ArrowLeft':
          newIndex = currentIndex - 1
          break
        default:
          return
      }

      e.preventDefault()
      items[newIndex].focus()
      items[newIndex].click()
    })

    for (const item of items) {
      item.addEventListener('click', () => {
        const src = item.getAttribute('data-gallery-src')
        const clickedIndex = items.indexOf(item)

        if (!isDesktop.matches) {
          for (const i of items) {
            i.setAttribute('aria-selected', 'false')
            i.tabIndex = -1
          }
          item.setAttribute('aria-selected', 'true')
          item.tabIndex = 0
        }

        if (isDesktop.matches) {
          if (src && zoomEl) {
            zoomEl.setAttribute('src', src)
            panzoom?.reset()
          }
          activeIndex = clickedIndex
          if (
            controlsPrev && controlsNext && controlsCurrent && controlsTotal
          ) {
            controlsCurrent!.textContent = pad(clickedIndex + 1)
            controlsPrev!.disabled = clickedIndex === 0
            controlsNext!.disabled = clickedIndex === total - 1
          }
          zoomDialog?.showModal()
        } else {
          if (src && packshotImg) {
            packshot?.querySelectorAll('source').forEach((s) => s.remove())
            packshotImg.src = src
            zoomEl.setAttribute('src', src)
          }
          activeIndex = clickedIndex
          if (
            controlsPrev && controlsNext && controlsCurrent && controlsTotal
          ) {
            controlsCurrent!.textContent = pad(clickedIndex + 1)
            controlsPrev!.disabled = clickedIndex === 0
            controlsNext!.disabled = clickedIndex === total - 1
          }
        }
      })
    }

    isDesktop.addEventListener('change', (e) => {
      if (e.matches) {
        productGallery.removeAttribute('role')
        for (const item of items) {
          item.removeAttribute('role')
          item.tabIndex = 0
        }
      } else {
        productGallery.setAttribute('role', 'tablist')
        const activeItem = items.find((i) =>
          i.getAttribute('aria-selected') === 'true'
        )
        for (const item of items) {
          item.setAttribute('role', 'tab')
          item.tabIndex = item === activeItem ? 0 : -1
        }
      }
    })

    if (controlsPrev && controlsNext && controlsCurrent && controlsTotal) {
      const setActive = (index: number) => {
        activeIndex = index
        const src = items[index].getAttribute('data-gallery-src')
        if (src && zoomEl) {
          zoomEl.setAttribute('src', src)
          panzoom.reset()
        }
        controlsCurrent!.textContent = pad(index + 1)
        controlsPrev!.disabled = index === 0
        controlsNext!.disabled = index === total - 1
      }

      controlsPrev!.addEventListener('click', () => {
        if (activeIndex > 0) setActive(activeIndex - 1)
      })

      controlsNext!.addEventListener('click', () => {
        if (activeIndex < total - 1) setActive(activeIndex + 1)
      })

      zoomDialog.addEventListener('close', () => {
        activeIndex = 0
        if (isDesktop.matches) {
          zoomEl.setAttribute('src', items[0].getAttribute('data-gallery-src'))
        }
        controlsCurrent!.textContent = pad(1)
        controlsPrev!.disabled = true
        controlsNext!.disabled = false
      })
    }
  }
}
